import type { ActionSection } from './actions/types'

/**
 * Named priority constants for command bar actions.
 * Higher values appear before lower values within the same section.
 * Mirrors the priority reference table in ARCHITECTURE.md.
 */
export const PRIORITIES = {
  createPost: 100, // Create New Post, Publish navigation
  ideas: 90, // Ideas navigation
  calendar: 85, // Calendar navigation, Create Idea
  community: 80, // Community navigation
  connectService: 80, // Service-specific Connect Channel (same tier as community)
  connectChannel: 75, // Generic Connect Channel
  analytics: 70, // Analytics navigation, single-channel submenus
  teamInvite: 70, // Invite Team Member (same tier as analytics)
  channelSettings: 65, // Channel Settings (single-channel mode)
  channels: 60, // Primary channel actions
  channelSubmenus: 50, // Channel submenus, Help Center
  sendFeedback: 45, // Send Feedback
  suggestFeature: 43, // Suggest Feature
  channelGroups: 40, // Channel groups, What's New
  savedViews: 38, // Saved views
  keyboardShortcuts: 35, // Keyboard Shortcuts
  settings: 30, // Tags, Settings (collapsed), Account settings
  orgSettings: 25, // Organization settings
  featureSettings: 20, // Feature settings
  otherSettings: 15, // Other settings
} as const

/**
 * Canonical ordering of sections in the command bar.
 * Used for both display ordering and sorting priority.
 */
export const SECTION_ORDER: ActionSection[] = [
  'recent',
  'quick-actions',
  'navigation',
  'saved-views',
  'channels',
  'connect-channel',
  'channel-groups',
  'tags',
  'settings',
  'help',
]

/**
 * Section display labels for command bar headings.
 */
export const SECTION_LABELS: Record<ActionSection, string> = {
  recent: 'Recent',
  'quick-actions': 'Quick Actions',
  navigation: 'Navigation',
  channels: 'Channels',
  'connect-channel': 'Connect Channel',
  'channel-groups': 'Channel Groups',
  'saved-views': 'Saved Views',
  tags: 'Tags',
  settings: 'Settings',
  help: 'Help',
}

/**
 * Derive a section priority map from SECTION_ORDER.
 * Higher index in SECTION_ORDER = shown first, so we reverse the weighting.
 */
export function getSectionPriority(section: ActionSection): number {
  const index = SECTION_ORDER.indexOf(section)
  if (index === -1) return 0
  return (SECTION_ORDER.length - index) * 10
}
